import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { OffenePostenResponse, OffenerPosten, Zahlung, ZahlungsArt } from '../types';
import './ZahlungList.css';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ZAHLUNGSARTEN: { value: ZahlungsArt; label: string }[] = [
  { value: 'UEBERWEISUNG', label: 'Überweisung' },
  { value: 'LASTSCHRIFT', label: 'Lastschrift' },
  { value: 'BAR', label: 'Bar' },
  { value: 'SCHECK', label: 'Scheck' },
  { value: 'VERRECHNUNG', label: 'Verrechnung' },
];

function formatEUR(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return err.message;
  }
  return 'Unbekannter Fehler';
}

interface BuchenForm {
  betrag: string;
  zahlungsart: ZahlungsArt;
  valuta_datum: string;
  verwendungszweck: string;
  bank_referenz: string;
  notizen: string;
}

function initialForm(posten: OffenerPosten): BuchenForm {
  return {
    betrag: posten.noch_offen.toFixed(2),
    zahlungsart: 'UEBERWEISUNG',
    valuta_datum: todayISO(),
    verwendungszweck: '',
    bank_referenz: '',
    notizen: '',
  };
}

const ZahlungList: React.FC = () => {
  const [offenePosten, setOffenePosten] = useState<OffenePostenResponse | null>(null);
  const [zahlungen, setZahlungen] = useState<Zahlung[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [buchenId, setBuchenId] = useState<number | null>(null);
  const [form, setForm] = useState<BuchenForm | null>(null);
  const [buchenLaedt, setBuchenLaedt] = useState(false);
  const [buchenFehler, setBuchenFehler] = useState<string | null>(null);
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFehler(null);
    setAkteId(null);
    try {
      const [postenRes, zahlungenRes] = await Promise.all([
        axios.get<OffenePostenResponse>(`${API_URL}/api/zahlungen/offene-posten`),
        axios.get<Zahlung[]>(`${API_URL}/api/zahlungen`),
      ]);
      setOffenePosten(postenRes.data);
      setZahlungen(zahlungenRes.data);
    } catch (err) {
      setFehler(`Zahlungen konnten nicht geladen werden: ${extractError(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openBuchen = (posten: OffenerPosten) => {
    setBuchenFehler(null);
    setBuchenId(posten.ausgangsrechnung_id);
    setForm(initialForm(posten));
  };

  const closeBuchen = () => {
    setBuchenId(null);
    setForm(null);
    setBuchenFehler(null);
  };

  const setField = (field: keyof BuchenForm, value: string) => {
    setForm(f => (f ? { ...f, [field]: value } : f));
  };

  const submitBuchen = async (posten: OffenerPosten) => {
    if (!form) return;
    const betragNum = parseFloat(form.betrag.replace(',', '.'));
    if (Number.isNaN(betragNum) || betragNum <= 0) {
      setBuchenFehler('Bitte einen gültigen Betrag angeben.');
      return;
    }
    setBuchenLaedt(true);
    setBuchenFehler(null);
    try {
      await axios.post(`${API_URL}/api/zahlungen`, {
        ausgangsrechnung_id: posten.ausgangsrechnung_id,
        betrag: betragNum,
        zahlungsart: form.zahlungsart,
        valuta_datum: form.valuta_datum,
        verwendungszweck: form.verwendungszweck || null,
        bank_referenz: form.bank_referenz || null,
        notizen: form.notizen || null,
      });
      closeBuchen();
      await load();
    } catch (err) {
      setBuchenFehler(extractError(err));
    } finally {
      setBuchenLaedt(false);
    }
  };

  if (loading) {
    return (
      <div className="zahlung-list-container">
        <div className="loading">Lädt...</div>
      </div>
    );
  }

  if (fehler) {
    return (
      <div className="zahlung-list-container">
        <div className="zl-error">
          <span>{fehler}</span>
          <button className="zl-retry-btn" onClick={load}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const posten = offenePosten?.posten ?? [];

  return (
    <div className="zahlung-list-container">
      <div className="list-header">
        <h2>Zahlungen</h2>
      </div>

      <section className="zl-section">
        <div className="zl-section-header">
          <h3>Offene Posten</h3>
          {offenePosten && (
            <div className="zl-summary">
              <span className="zl-summary-item">Gesamt offen: <strong>{formatEUR(offenePosten.gesamt_offen)}</strong></span>
              <span className="zl-summary-item zl-summary-warn">Überfällig: <strong>{formatEUR(offenePosten.ueberfaellig)}</strong></span>
            </div>
          )}
        </div>

        {posten.length === 0 ? (
          <div className="empty-state">
            <p>Keine offenen Posten vorhanden</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="zahlung-table">
              <thead>
                <tr>
                  <th>Rechnung</th>
                  <th>Kunde</th>
                  <th>Brutto</th>
                  <th>Bezahlt</th>
                  <th>Offen</th>
                  <th>Fällig</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {posten.map(p => (
                  <React.Fragment key={p.ausgangsrechnung_id}>
                    <tr className={p.ueberfaellig ? 'zl-row-ueberfaellig' : ''}>
                      <td className="zahlung-nr">{p.rechnungsnummer}</td>
                      <td>{p.kunde_name || '-'}</td>
                      <td className="price-cell">{formatEUR(p.brutto_gesamt)}</td>
                      <td className="price-cell">{formatEUR(p.bereits_bezahlt)}</td>
                      <td className="price-cell">{formatEUR(p.noch_offen)}</td>
                      <td>
                        {p.ueberfaellig ? (
                          <span className="zl-ueberfaellig-badge">{formatDate(p.faelligkeitsdatum)}</span>
                        ) : formatDate(p.faelligkeitsdatum)}
                      </td>
                      <td className="action-cell">
                        <button
                          className="btn-zahlung-buchen"
                          onClick={() => (buchenId === p.ausgangsrechnung_id ? closeBuchen() : openBuchen(p))}
                        >
                          {buchenId === p.ausgangsrechnung_id ? 'Abbrechen' : 'Zahlung buchen'}
                        </button>
                      </td>
                    </tr>
                    {buchenId === p.ausgangsrechnung_id && form && (
                      <tr className="zl-buchen-row">
                        <td colSpan={7}>
                          <div className="zl-buchen-form">
                            <label>
                              Betrag (€)
                              <input
                                type="number"
                                step="0.01"
                                value={form.betrag}
                                onChange={e => setField('betrag', e.target.value)}
                              />
                            </label>
                            <label>
                              Zahlungsart
                              <select
                                value={form.zahlungsart}
                                onChange={e => setField('zahlungsart', e.target.value)}
                              >
                                {ZAHLUNGSARTEN.map(za => (
                                  <option key={za.value} value={za.value}>{za.label}</option>
                                ))}
                              </select>
                            </label>
                            <label>
                              Valutadatum
                              <input
                                type="date"
                                value={form.valuta_datum}
                                onChange={e => setField('valuta_datum', e.target.value)}
                              />
                            </label>
                            <label>
                              Verwendungszweck
                              <input
                                type="text"
                                value={form.verwendungszweck}
                                onChange={e => setField('verwendungszweck', e.target.value)}
                              />
                            </label>
                            <label>
                              Bank-Referenz
                              <input
                                type="text"
                                value={form.bank_referenz}
                                onChange={e => setField('bank_referenz', e.target.value)}
                              />
                            </label>
                            <label className="zl-form-notizen">
                              Notizen
                              <input
                                type="text"
                                value={form.notizen}
                                onChange={e => setField('notizen', e.target.value)}
                              />
                            </label>
                            {buchenFehler && <div className="zl-inline-fehler">{buchenFehler}</div>}
                            <div className="zl-form-actions">
                              <button
                                className="btn-zahlung-confirm"
                                onClick={() => submitBuchen(p)}
                                disabled={buchenLaedt}
                              >
                                {buchenLaedt ? 'Bucht...' : 'Buchen'}
                              </button>
                              <button className="btn-zahlung-cancel" onClick={closeBuchen}>
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="zl-section">
        <div className="zl-section-header">
          <h3>Zahlungseingänge</h3>
        </div>
        {zahlungen.length === 0 ? (
          <div className="empty-state">
            <p>Keine Zahlungen vorhanden</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="zahlung-table">
              <thead>
                <tr>
                  <th>Buchungsdatum</th>
                  <th>Betrag</th>
                  <th>Zahlungsart</th>
                  <th>Valuta</th>
                  <th>Verwendungszweck</th>
                  <th>Skonto</th>
                  <th>Rechnung</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {zahlungen.map(z => (
                  <React.Fragment key={z.zahlung_id}>
                    <tr>
                      <td>{formatDate(z.buchungsdatum)}</td>
                      <td className="price-cell">{formatEUR(z.betrag)}</td>
                      <td>{ZAHLUNGSARTEN.find(za => za.value === z.zahlungsart)?.label || z.zahlungsart}</td>
                      <td>{formatDate(z.valuta_datum)}</td>
                      <td>{z.verwendungszweck || '-'}</td>
                      <td>
                        {z.skonto_genutzt ? (
                          <span className="zl-skonto-badge">{formatEUR(z.skonto_betrag)}</span>
                        ) : '-'}
                      </td>
                      <td>
                        {z.ausgangsrechnung_id ? `AR #${z.ausgangsrechnung_id}` : z.eingangsrechnung_id ? `ER #${z.eingangsrechnung_id}` : '-'}
                      </td>
                      <td className="action-cell">
                        <button
                          className="btn-status-update"
                          onClick={() => setAkteId(akteId === z.zahlung_id ? null : z.zahlung_id)}
                        >
                          {akteId === z.zahlung_id ? 'Akte schließen' : 'Akte'}
                        </button>
                      </td>
                    </tr>
                    {akteId === z.zahlung_id && (
                      <tr>
                        <td colSpan={8} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                          <AktivitaetenBlock entitaetTyp="zahlung" entitaetId={z.zahlung_id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default ZahlungList;
